import { defineStore } from "pinia";
import { ref, watch } from "vue";

const STORAGE_KEY = "pushka_settings";

export type Stage2ModelId = "transformer_sed" | "transformer_sed_v2" | "mobilenet_distilled" | "conformer_sed_v3" | "conformer_sed_v4" | "tcn_sed_v2" | "efficientat_sed" | "efficientat_sed_v2" | "efficientat_sed_v5" | "efficientat_sed_v6" | "efficientat_sed_v9" | "efficientat_sed_v13" | "efficientat_sed_v14" | "efficientat_sed_v16" | "efficientat_sed_v17" | "mobilenet_ast_v4" | "attention_mobilenet_v6" | "panns_cnn14_v3" | "gru_sed_v2" | "conformer_sed_v4_mel128" | "tcn_sed_v2_mel128" | "panns_onset_bce_v1" | "gru_onset_bce_v1" | "efficientat_onset_bce_v1" | "conformer_onset_bce_v1" | "attnmn_gru_onset_bce_v1" | "attnmn_tcn_onset_bce_v3";
export type Stage1ModelId = "m1_light" | "m1_full" | "m1_full_v2";

/**
 * Battery-saving behaviour tied to the air-raid alert status around the user:
 * - "normal"    — alerts are ignored, detector always runs as-is;
 * - "adaptive"  — always runs, but Stage-1 threshold is raised while there is no alert;
 * - "alertOnly" — mic + cascade run only while an alert is active nearby.
 */
export type PowerMode = "normal" | "adaptive" | "alertOnly";

export interface AppSettings {
  // ⚠️ Known issue (2026-08): `geoMode` and `alwaysWatchLocation` are persisted
  // and bound to the SettingsPanel UI, but are NOT read by any geolocation logic
  // — the live radar (useRadarLogic) drives behaviour from its own `liveTracking`
  // toggle + `locateOnce` action instead. These are effectively ghost settings.
  // Follow-up: either wire them into useRadarLogic or remove them from the UI.
  geoMode: "automatic" | "manual";
  alwaysWatchLocation: boolean; // continuous GPS watch in background
  autoStartMonitoring: boolean; // start audio monitoring automatically on open
  permissionMessageShown: boolean; // whether the microphone permission message has been shown
  // Pipeline stage toggles
  pipelineEnergyFilterEnabled: boolean;
  pipelineAnomalyDetectorEnabled: boolean;
  pipelineRefinerEnabled: boolean;
  // Stage 1 model selection
  stage1Model: Stage1ModelId;
  // Stage 2 model selection
  stage2Model: Stage2ModelId;
  // Подход 3: модель экспоненциального спада
  decayModelEnabled: boolean;
  decayModelTolerance: number;
  // Энергосбережение по статусу воздушной тревоги
  powerMode: PowerMode;
  // Сжимать сохраняемые аудио-клипы (Opus/WebM, lossy). По умолчанию true — экономит ~8× места
  // в IndexedDB. Выключить, чтобы хранить lossless 16-bit WAV на частоте детектора: тогда
  // выгрузка «для анализа» байт-в-байт совпадает со входом модели (нужно для серверного
  // ре-анализа и как чистые данные обучения).
  audioClipCompression: boolean;
  // Автоматически отправлять WAV-клип + полный трейс анализа (report) на сервер для каждого
  // подтверждённого события сразу после захвата клипа (~3 c). По умолчанию false — приватность
  // by-default: сырой звук микрофона покидает устройство только при явном согласии. Это
  // единственный авто-путь, который шлёт аудио (ручная кнопка «Отправить для анализа» — второй).
  audioClipAutoUpload: boolean;
}

const DEFAULTS: AppSettings = {
  geoMode: "automatic",
  alwaysWatchLocation: true,
  autoStartMonitoring: false,
  permissionMessageShown: false,
  pipelineEnergyFilterEnabled: true,
  pipelineAnomalyDetectorEnabled: false,
  pipelineRefinerEnabled: true,
  // Best recent defaults: M1-full (verification + classification, 128 mel, val_class_acc 90.3%)
  // and AttnMN-GRU onset-BCE (best onset: MAE 45.5 ms, F1@50 0.767, Pearson 0.955, 93.4% acc,
  // 356 KB). Sub-frame parabolic decoding in onnxModels.ts sharpens it further. WASM-only (BiGRU).
  stage1Model: "m1_full",
  stage2Model: "attnmn_gru_onset_bce_v1",
  decayModelEnabled: false,
  decayModelTolerance: 0.20,
  powerMode: "normal",
  audioClipCompression: true,
  audioClipAutoUpload: false,
};

function loadFromStorage(): AppSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return { ...DEFAULTS, ...JSON.parse(raw) };
    }
  } catch {
    // ignore parse errors
  }
  return { ...DEFAULTS };
}

function saveToStorage(settings: AppSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore storage errors (e.g. private mode quota)
  }
}

export const useSettingsStore = defineStore("settings", () => {
  const saved = loadFromStorage();

  const geoMode = ref<"automatic" | "manual">(saved.geoMode);
  const alwaysWatchLocation = ref<boolean>(saved.alwaysWatchLocation);
  const autoStartMonitoring = ref<boolean>(saved.autoStartMonitoring);
  const permissionMessageShown = ref<boolean>(saved.permissionMessageShown);
  const pipelineEnergyFilterEnabled = ref<boolean>(saved.pipelineEnergyFilterEnabled);
  const pipelineAnomalyDetectorEnabled = ref<boolean>(saved.pipelineAnomalyDetectorEnabled);
  const pipelineRefinerEnabled = ref<boolean>(saved.pipelineRefinerEnabled);
  const stage1Model = ref<Stage1ModelId>(saved.stage1Model ?? "m1_full");
  const stage2Model = ref<Stage2ModelId>(saved.stage2Model ?? "attnmn_gru_onset_bce_v1");
  const decayModelEnabled = ref<boolean>(saved.decayModelEnabled ?? false);
  const decayModelTolerance = ref<number>(saved.decayModelTolerance ?? 0.20);
  const powerMode = ref<PowerMode>(saved.powerMode ?? "normal");
  const audioClipCompression = ref<boolean>(saved.audioClipCompression ?? false);
  const audioClipAutoUpload = ref<boolean>(saved.audioClipAutoUpload ?? false);

  // Auto-persist on any change
  watch(
    [geoMode, alwaysWatchLocation, autoStartMonitoring, permissionMessageShown,
     pipelineEnergyFilterEnabled, pipelineAnomalyDetectorEnabled, pipelineRefinerEnabled,
     stage1Model, stage2Model, decayModelEnabled, decayModelTolerance, powerMode,
     audioClipCompression, audioClipAutoUpload],
    () => {
      saveToStorage({
        geoMode: geoMode.value,
        alwaysWatchLocation: alwaysWatchLocation.value,
        autoStartMonitoring: autoStartMonitoring.value,
        permissionMessageShown: permissionMessageShown.value,
        pipelineEnergyFilterEnabled: pipelineEnergyFilterEnabled.value,
        pipelineAnomalyDetectorEnabled: pipelineAnomalyDetectorEnabled.value,
        pipelineRefinerEnabled: pipelineRefinerEnabled.value,
        stage1Model: stage1Model.value,
        stage2Model: stage2Model.value,
        decayModelEnabled: decayModelEnabled.value,
        decayModelTolerance: decayModelTolerance.value,
        powerMode: powerMode.value,
        audioClipCompression: audioClipCompression.value,
        audioClipAutoUpload: audioClipAutoUpload.value,
      });
    },
  );

  function setGeoMode(mode: "automatic" | "manual"): void {
    geoMode.value = mode;
  }

  function setAlwaysWatchLocation(value: boolean): void {
    alwaysWatchLocation.value = value;
  }

  function setAutoStartMonitoring(value: boolean): void {
    autoStartMonitoring.value = value;
  }

  function setPermissionMessageShown(value: boolean): void {
    permissionMessageShown.value = value;
  }

  function setPipelineEnergyFilterEnabled(value: boolean): void {
    pipelineEnergyFilterEnabled.value = value;
  }

  function setPipelineAnomalyDetectorEnabled(value: boolean): void {
    pipelineAnomalyDetectorEnabled.value = value;
  }

  function setPipelineRefinerEnabled(value: boolean): void {
    pipelineRefinerEnabled.value = value;
  }

  function setStage1Model(value: Stage1ModelId): void {
    stage1Model.value = value;
  }

  function setStage2Model(value: Stage2ModelId): void {
    stage2Model.value = value;
  }

  function setDecayModelEnabled(value: boolean): void {
    decayModelEnabled.value = value;
  }

  function setDecayModelTolerance(value: number): void {
    decayModelTolerance.value = value;
  }

  function setPowerMode(value: PowerMode): void {
    powerMode.value = value;
  }

  function setAudioClipCompression(value: boolean): void {
    audioClipCompression.value = value;
  }

  function setAudioClipAutoUpload(value: boolean): void {
    audioClipAutoUpload.value = value;
  }

  function reset(): void {
    geoMode.value = DEFAULTS.geoMode;
    alwaysWatchLocation.value = DEFAULTS.alwaysWatchLocation;
    autoStartMonitoring.value = DEFAULTS.autoStartMonitoring;
    permissionMessageShown.value = DEFAULTS.permissionMessageShown;
    pipelineEnergyFilterEnabled.value = DEFAULTS.pipelineEnergyFilterEnabled;
    pipelineAnomalyDetectorEnabled.value = DEFAULTS.pipelineAnomalyDetectorEnabled;
    pipelineRefinerEnabled.value = DEFAULTS.pipelineRefinerEnabled;
    stage1Model.value = DEFAULTS.stage1Model;
    stage2Model.value = DEFAULTS.stage2Model;
    decayModelEnabled.value = DEFAULTS.decayModelEnabled;
    decayModelTolerance.value = DEFAULTS.decayModelTolerance;
    powerMode.value = DEFAULTS.powerMode;
    audioClipCompression.value = DEFAULTS.audioClipCompression;
    audioClipAutoUpload.value = DEFAULTS.audioClipAutoUpload;
  }

  return {
    geoMode,
    alwaysWatchLocation,
    autoStartMonitoring,
    permissionMessageShown,
    pipelineEnergyFilterEnabled,
    pipelineAnomalyDetectorEnabled,
    pipelineRefinerEnabled,
    stage1Model,
    stage2Model,
    setGeoMode,
    setAlwaysWatchLocation,
    setAutoStartMonitoring,
    setPermissionMessageShown,
    setPipelineEnergyFilterEnabled,
    setPipelineAnomalyDetectorEnabled,
    setPipelineRefinerEnabled,
    setStage1Model,
    setStage2Model,
    decayModelEnabled,
    decayModelTolerance,
    setDecayModelEnabled,
    setDecayModelTolerance,
    powerMode,
    setPowerMode,
    audioClipCompression,
    setAudioClipCompression,
    audioClipAutoUpload,
    setAudioClipAutoUpload,
    reset,
  };
});
